import React, { FC } from "react";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { WebShopIcon } from "../../WebShopIcon";
import { Button } from "../../Button";
import { MobileRecaculateColumnDrawerProps } from "../lib/type";
import { isEmpty, isNil } from "lodash";
import { SelectField } from "../../Fields/SelectField";
import { getColumnCell, getFieldName } from "../lib/utils";
import { strings } from "@/src/shared/strings";
import { useFormContext } from "react-hook-form";
import { ProposalTableColumnFieldTypes } from "@/src/shared/schemas";
import { useProposalTableContext } from "./ProposalTableProvider";

export const MobileRecaculateColumnDrawer: FC<
  MobileRecaculateColumnDrawerProps
> = ({ isOpen, activeColumnIndex, proposalData }) => {
  const {getValues} = useFormContext()
  const {
    form: { control },
    onCurrencySelectChange,
    onRecalculate,
    onCancel,
    activeColumnId,
    rows,
    isTariffCalcPending
  } = useProposalTableContext();
  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={() => onCancel()}
      disableScrollLock
      disablePortal
      className="hidden max-mdPlus:flex"
    >
      <Box className="flex flex-col gap-4 rounded-t-2xl bg-white p-4">
        <Box className="justify-between flex-center-row">
          <Typography variant="h1" className="block text-center text-xl">
            {strings.proposalTable.changeCover}
          </Typography>
          <IconButton onClick={() => onCancel()}>
            <WebShopIcon name="close" className="h-5 w-5 fill-blue" />
          </IconButton>
        </Box>
        {rows?.map((row, rowIndex) => {
          const columnCell = getColumnCell({
            proposalData,
            rowKey: row?.key,
            columnIndex: activeColumnIndex,
            daysAbroad: getValues("daysAbroad")
          });

          if (!columnCell || isEmpty(columnCell?.fields)) return null;

          const columnFields = columnCell.fields.filter(
            ({ type }) =>
              type === ProposalTableColumnFieldTypes.SelectNumber ||
              type === ProposalTableColumnFieldTypes.SelectCurrency ||
              type === ProposalTableColumnFieldTypes.SelectNumberCurrency,
          );

          if( row?.key === "liability_insurance" && getValues("daysAbroad") <= 30){
            return null
          }

          if (!isEmpty(columnFields)) {
            return (
              <Stack className="flex flex-col gap-4" key={rowIndex}>
                <Stack className="flex w-full flex-col gap-2">
                  <Typography>{row?.title}</Typography>
                  {columnFields.map((fieldData, fieldIndex) => {
                    if (
                      fieldData.type ===
                        ProposalTableColumnFieldTypes.SelectCurrency ||
                      fieldData.type ===
                        ProposalTableColumnFieldTypes.SelectNumber ||
                      fieldData.type ===
                        ProposalTableColumnFieldTypes.SelectNumberCurrency
                    ) {
                      if (activeColumnId) {
                        const name = getFieldName({
                          columnId: activeColumnId,
                          fieldName: fieldData.name,
                        });
                        return (
                          <SelectField
                            key={fieldIndex}
                            control={control}
                            name={name}
                            readOnly={!row.editable}
                            disabled={(activeColumnId === "Vip" && fieldData.name === "helpAssistance_Suminsured" && getValues("daysAbroad") < 120)}
                            options={fieldData?.options ?? []}
                            sx={{
                              "& .MuiInputBase-root": {
                                "& .MuiInputBase-input": {
                                  padding: "10px 11px 10px 11px !important",
                                },
                              },
                            }}
                            onChange={(_, value) => {
                              if (
                                fieldData.type ===
                                ProposalTableColumnFieldTypes.SelectCurrency
                              ) {
                                onCurrencySelectChange?.({ value });
                              }
                            }}
                          />
                        );
                      }
                    }
                    return null;
                  })}
                </Stack>
              </Stack>
            );
          }
          return null;
        })}
        <Stack className="gap-2 flex-center-row">
          <Button variant="outlined" onClick={onCancel} className="w-full" disabled={isTariffCalcPending}>
            {strings.common.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              !isNil(activeColumnIndex) &&
              !isNil(activeColumnId) &&
              onRecalculate(activeColumnIndex, activeColumnId)
            }
            loading={isTariffCalcPending}
            className="w-full"
          >
            {strings.proposalTable.recalculate}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
